import React, { useEffect, useRef, useState } from 'react';
import { useSupabase } from '@/lib/SupabaseProvider';
import { useOrgCore } from '@/lib/OrgCoreProvider';
import { useBilling } from '@/lib/BillingProvider';
import { resolvePlanTier } from '@/lib/planConfig';
import {
  clearPostHogOrganization,
  syncPostHogOrganization,
} from './frontend';

/**
 * Synchronizes authenticated organization state into PostHog.
 *
 * The bridge deliberately renders nothing. It must be mounted inside both the
 * core organization and billing providers. Incomplete or mismatched billing
 * context clears analytics grouping so an organization switch cannot retain
 * the previous organization's plan or flag evaluation.
 */
export const PostHogOrganizationBridge: React.FC = () => {
  const { user } = useSupabase();
  const {
    activeOrg,
    memberships,
    userRole,
    isLoading: coreLoading,
  } = useOrgCore();
  const { orgBilling, billingLoading } = useBilling();

  const userId = user?.id ?? null;
  const organizationId = activeOrg?.id ?? null;
  const organizationName = activeOrg?.name ?? null;
  const matchingMembershipId = memberships.find(
    (membership) => (
      membership.user_id === userId
      && membership.organization_id === organizationId
    ),
  )?.id ?? null;
  const hasBillingRecord = orgBilling !== null;
  const billingOrganizationId = orgBilling?.organization_id ?? null;
  const planId = orgBilling?.plan_id ?? null;
  const previousOrganizationIdRef = useRef(organizationId);
  const pendingBillingOrganizationIdRef = useRef<string | null>(null);
  const observedBillingLoadOrganizationIdRef = useRef<string | null>(null);
  const [organizationRevision, setOrganizationRevision] = useState(0);

  useEffect(() => {
    if (previousOrganizationIdRef.current !== organizationId) {
      previousOrganizationIdRef.current = organizationId;
      pendingBillingOrganizationIdRef.current = organizationId;
      observedBillingLoadOrganizationIdRef.current = null;
      clearPostHogOrganization();
      setOrganizationRevision((revision) => revision + 1);
      return;
    }

    if (billingLoading && organizationId) {
      observedBillingLoadOrganizationIdRef.current = organizationId;
    }

    const billingMatchesActiveOrganization = (
      !billingLoading
      && (!hasBillingRecord || billingOrganizationId === organizationId)
    );

    if (pendingBillingOrganizationIdRef.current === organizationId) {
      const matchingBillingRowLoaded = billingOrganizationId === organizationId;
      const completedBillingLoad = (
        observedBillingLoadOrganizationIdRef.current === organizationId
        && !billingLoading
      );
      if (!matchingBillingRowLoaded && !completedBillingLoad) {
        clearPostHogOrganization();
        return;
      }
      pendingBillingOrganizationIdRef.current = null;
    }

    if (
      !userId
      || !organizationId
      || !organizationName
      || coreLoading
      || !matchingMembershipId
      || !userRole
      || !billingMatchesActiveOrganization
    ) {
      clearPostHogOrganization();
      return;
    }

    syncPostHogOrganization({
      organizationId,
      organizationName,
      organizationPlan: resolvePlanTier(planId),
      organizationRole: userRole,
    });
  }, [
    userId,
    organizationId,
    organizationName,
    coreLoading,
    matchingMembershipId,
    userRole,
    hasBillingRecord,
    billingOrganizationId,
    planId,
    billingLoading,
    organizationRevision,
  ]);

  return null;
};

export default PostHogOrganizationBridge;
